import React from 'react';
import { CSSProperties } from 'react';
import { SVGProps } from 'react';

import { Box, SvgIconPropsSizeOverrides, SvgIconTypeMap, useTheme } from '@mui/material';
import { Menu
    , Info
    , CommentBank
    , CameraOutdoor
    , YouTube
    , Home
    , Login
    , Announcement
    , DeviceUnknown
    , Error
    , Warning
    , Help
    , PriorityHigh
    , CheckCircle
    , Cancel
    , Close
    , SearchOutlined
    , ExpandMore
    , Star
    , Save
    , Description
    , Photo
    , SsidChart
    , Tune
    , ExpandLess
    , Cloud
    , Check
    , TextIncrease
    , TextDecrease
    , Edit
    , AspectRatio
    , VolumeOffRounded
    , VolumeMuteRounded
    , VolumeUpRounded
    , VolumeDownRounded
    , TocRounded
    , ZoomOutRounded
    , ZoomInRounded
    , BrightnessHigh
    , BrightnessLow
    , LightMode
    , Thermostat
    , Add
    , Remove
    , WifiOff
    , MarkChatUnread
    , CenterFocusWeakTwoTone
    , RestartAlt
    , GridViewOutlined
    , DonutLargeOutlined
    , PowerSettingsNew
    , Power
    , PowerOff
    , DnsOutlined
    , VpnKey
} from '@mui/icons-material';
import { OverridableComponent } from '@mui/material/OverridableComponent';

const iconMapping: { [key: string]: OverridableComponent<SvgIconTypeMap<{}, "svg">> } = {
    CommentBank: CommentBank,
    CameraOutdoor: CameraOutdoor,
    YouTube: YouTube,
    Home: Home,
    Login: Login,
    Menu: Menu,
    Error: Error,
    Announcement: Announcement,
    Warning: Warning,
    Help: Help,
    PriorityHigh: PriorityHigh,
    CheckCircle: CheckCircle,
    Cancel: Cancel,
    Info: Info,
    Close: Close,
    SearchOutlined: SearchOutlined,
    ExpandMore: ExpandMore,
    ExpandLess: ExpandLess,
    Star: Star,
    Save: Save,
    Description: Description,
    Photo: Photo,
    SsidChart: SsidChart,
    Tune: Tune,
    Cloud: Cloud,
    Check: Check,
    TextIncrease:TextIncrease,
    TextDecrease:TextDecrease,
    Edit:Edit,
    AspectRatio:AspectRatio,
    VolumeOffRounded:VolumeOffRounded,
    VolumeMuteRounded:VolumeMuteRounded,
    VolumeUpRounded:VolumeUpRounded,
    VolumeDownRounded:VolumeDownRounded,
    TocRounded:TocRounded,
    ZoomOutRounded:ZoomOutRounded,
    ZoomInRounded:ZoomInRounded,
    BrightnessHigh:BrightnessHigh,
    BrightnessLow:BrightnessLow,
    LightMode:LightMode,
    Thermostat:Thermostat,
    Add:Add,
    Remove:Remove,
    WifiOff:WifiOff,
    MarkChatUnread:MarkChatUnread,
    CenterFocusWeakTwoTone:CenterFocusWeakTwoTone,
    RestartAlt:RestartAlt,
    GridViewOutlined:GridViewOutlined,
    DonutLargeOutlined:DonutLargeOutlined,
    PowerSettingsNew:PowerSettingsNew,
    Power:Power,
    PowerOff:PowerOff,
    DnsOutlined:DnsOutlined,
    VpnKey:VpnKey,
  };
const MUIColors = ["inherit", "disabled", "action", "error", "primary", "secondary", "info", "success", "warning"] as const;
type MUIColor = typeof MUIColors[number] | 'contrast';
type IconName = keyof typeof iconMapping;

type FontSize = "small" | "inherit" | "large" | "medium";

type IconWrapperProps = {
    color?: string;
    fontSize?: FontSize;
    children: React.ReactNode;
    style?: React.CSSProperties;
    boxed?:boolean;
    onClick?: React.MouseEventHandler<HTMLDivElement>;
};

const IconWrapper: React.FC<IconWrapperProps> = ({ color, fontSize, children, style, boxed=false,onClick }) => {
    const theme = useTheme();
    const isPredefinedColor = color && MUIColors.includes(color as Exclude<MUIColor, 'contrast'>);
    const isThemeColor = color === "contrast";

    const boxStyle:React.CSSProperties = {
        backgroundColor: 'transparent',
        border: '1px solid #AAA', 
        borderRadius: '3px', 
        alignItems: "center",
        justifyContent: "center",
    }
  
    return (
        <Box
            component="span"
            onClick={onClick}
            sx={{
                color: isThemeColor ? theme.palette.primary.contrastText : isPredefinedColor ? undefined : color,
                cursor: 'pointer',
                ...style,
                ...(boxed ? boxStyle : {}),
                
            }}
        >
            {React.cloneElement(children as React.ReactElement, {
                color: isPredefinedColor ? (color as Exclude<MUIColor, 'contrast'>) || "secondary" : undefined,
                fontSize: fontSize || "inherit",
                style: { ...style },
            })}
        </Box>
    );
};


interface OrbotnikIconProps {
    iconName?: IconName;
    icon?: React.ReactElement<SvgIconProps>; 
    color?: string;
    fontSize?: FontSize;
    isResponsive?: boolean; 
    style?: React.CSSProperties
    boxed?: boolean
    onClick?: React.MouseEventHandler<HTMLDivElement>;
}
const OrbotnikIcon: React.FC<OrbotnikIconProps> = ({
    iconName,
    icon,
    color,
    fontSize,
    isResponsive,
    style,
    boxed=false,
    onClick
}) => {
    const responsiveStyle = isResponsive ? { width: '100%', height: '100%' } : {};
    if (icon) {
        return (
        <IconWrapper color={color} fontSize={fontSize} style={{...responsiveStyle, ...style}} boxed={boxed} onClick={onClick}>
            {React.cloneElement(icon, {
                style: { fontSize: 'inherit', ...responsiveStyle, ...style }, 
            })}
        </IconWrapper>
        )
    }
    const IconComponent = iconName ? iconMapping[iconName] || DeviceUnknown : DeviceUnknown;
    return (
        <IconWrapper color={color} fontSize={fontSize} style={{...responsiveStyle, ...style}} boxed={boxed} onClick={onClick}>
            <IconComponent style={{ fontSize: 'inherit', ...responsiveStyle, ...style }} />
        </IconWrapper>
    );
};
interface SvgIconProps {
    style?: CSSProperties;
}
export default OrbotnikIcon;

type OrbotnikCustomIconProps = {
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>> | string | React.ReactNode;
    color?: string;
    fontSize?: FontSize; 
    style?: React.CSSProperties;
    alt?: string;
    boxed?:boolean;
    onClick?: React.MouseEventHandler<HTMLDivElement>;
};
export const OrbotnikCustomIcon: React.FC<OrbotnikCustomIconProps> = ({
    icon,
    color,
    fontSize,
    style, 
    alt="Custom Icon",
    boxed,
    onClick
}) => {
    // Check if the icon is a React component type
    const isSvgIconComponent = typeof icon === 'function';

    // Create the icon element (SVG, IMG, or JSX)
    const iconElement = isSvgIconComponent ? (
        React.createElement(icon as React.ComponentType<SvgIconProps>, { style })
    ) : (
        // Check if icon is a React node (JSX element)
        React.isValidElement(icon) ? icon : <img src={icon as string} alt={alt} style={style} />
    );

    return (
        <IconWrapper color={color} fontSize={fontSize} style={style} boxed={boxed} onClick={onClick}>
            {iconElement}
        </IconWrapper>
    );
};
